import {
	useCouponMutation,
	useManageCouponMutation,
} from '../../store/api/api';
import { useForm } from 'react-hook-form';
import useManageHook from '../../components/hooks/useManageHook';
import { joiResolver } from '@hookform/resolvers/joi';
import {
	couponInterface,
	couponSchema,
	couponInitialized,
} from '@toriosoftware/torioshared/dist/coupon/Coupon';
import MainDropdownController from '../../components/dropdowns/MainDropdownController';
import MainInputController from '../../components/inputs/MainInputController';
import FormContainer from '../../components/containers/FormContainer';
import PageContainer from '../../components/containers/PageContainer';
import { GET_ROUTE_COMPONENT } from '../../config/config';
import {
	IMAGE_TYPES,
	ROUTE_NAMES,
	ROUTE_TYPES,
} from '@toriosoftware/torioshared/dist/config';
import MainSwitchController from '../../components/switch/MainSwitchController';
import SubHeader from '../../components/texts/SubHeader';
import React, { useEffect } from 'react';
import MainImageLinksController from '../../components/images/MainImageLinksController';

const Coupon = ({ user }) => {
	// State
	const [coupon] = useCouponMutation();
	const [manageCoupon] = useManageCouponMutation();
	// REMOVED: const [isPercentage, setIsPercentage] = React.useState(true);

	// POST DATA
	const {
		formState: { isDirty },
		handleSubmit,
		control,
		reset,
		watch,
		setValue,
	} = useForm<couponInterface>({
		defaultValues: { ...couponInitialized, type: 'PRICE' },
		shouldFocusError: true,
		mode: 'onChange',
		// resolver: joiResolver(couponSchema),
	});

	const type = watch('type');

	// GET DATA
	const {
		modelState,
		setIsLoaded,
		isLoaded,
		history,
		isNew,
		id,
		data,
		csrfToken,
	} = useManageHook({
		isLoadedInitial: false,
		retrieveData: [
			GET_ROUTE_COMPONENT(ROUTE_NAMES.coupon, user)?.data?.get(coupon, reset),
		],
		user: user,
	});

	useEffect(() => {
		if (type === 'PRICE') {
			setValue('percentage', undefined);
			setValue('twoForOne', false);
		} else if (type === 'PERCENTAGE') {
			setValue('amount', undefined);
			setValue('originalAmount', undefined);
			setValue('twoForOne', false);
		} else if (type === 'TWO_FOR_ONE') {
			setValue('percentage', undefined);
			setValue('amount', undefined);
			setValue('originalAmount', undefined);
			setValue('twoForOne', true);
		}
	}, [type, setValue]);

	const imageLink = watch('imageLink');

	return (
		<PageContainer breadPlaceholder={'Coupon'} header={'Coupon'}>
			<FormContainer
				csrfToken={csrfToken}
				isDirty={isDirty}
				modal={modelState}
				handleSubmit={handleSubmit}
				showBack={true}
				onSubmitFunction={manageCoupon}
				onRemoveFunction={manageCoupon}
				linkAfterDelete={'coupons'}
				title={'Coupon'}
				linkAfterSuccess={isNew ? ROUTE_TYPES[ROUTE_NAMES.coupons].route : ''}
				customData={{ id }}
				isLoaded={isLoaded}
				goBackDepth={0}
				history={history}
				hideRemove={isNew ? true : false}
			>
				<MainInputController
					control={control}
					label={'Name'}
					identifier={'name'}
					md={12}
				/>

				<MainInputController
					control={control}
					label={'Description'}
					rows={'2'}
					identifier={'description'}
					md={12}
				/>

				<MainDropdownController
					label={'Coupon Type'}
					identifier={'type'}
					options={[
						{ value: 'PRICE', text: 'Price Discount' },
						{ value: 'PERCENTAGE', text: 'Percentage Discount' },
						{ value: 'TWO_FOR_ONE', text: '2 for 1' },
					]}
					control={control}
					optionsValueKey={'value'}
					optionsTextKey={'text'}
					id={'coupon-type-select'}
					md={12}
				/>

				{type === 'PERCENTAGE' && (
					<MainInputController
						control={control}
						label={'Discount percentage'}
						type={'number'}
						identifier={'percentage'}
						md={12}
						options={{
							max: 100,
							min: 0,
						}}
						endText={'%'}
					/>
				)}

				{type === 'PRICE' && (
					<>
						<MainInputController
							control={control}
							label={'Original price (optional)'}
							type={'number'}
							identifier={'originalAmount'}
							md={6}
							options={{
								max: 30000,
								min: 0,
							}}
							endText={' kr'}
						/>
						<MainInputController
							control={control}
							label={'New price (optional)'}
							type={'number'}
							identifier={'amount'}
							md={6}
							options={{
								max: 30000,
								min: 0,
							}}
							endText={' kr'}
						/>
					</>
				)}

				<MainSwitchController
					key={21413}
					control={control}
					label={'One time use only'}
					md={12}
					identifier={'oneTimeUse'}
				/>

				<MainSwitchController
					key={21412}
					control={control}
					label={'Display below main card in Barhopp'}
					md={12}
					identifier={'isMain'}
				/>

				<SubHeader text={'Is available on'} />

				<MainInputController
					control={control}
					label={'From (optional)'}
					type={'text'}
					identifier={'from'}
					md={6}
				/>

				<MainInputController
					control={control}
					label={'To (optional)'}
					type={'text'}
					identifier={'to'}
					md={6}
				/>

				{[
					'Monday',
					'Tuesday',
					'Wednesday',
					'Thursday',
					'Friday',
					'Saturday',
					'Sunday',
				].map((day, index) => {
					return (
						<MainSwitchController
							key={index}
							control={control}
							label={day}
							md={3}
							identifier={day.toLowerCase()}
						/>
					);
				})}

				<MainImageLinksController
					control={control}
					maxSize={IMAGE_TYPES['coupon'].maxSize}
					text={'Upload an image of this coupon'}
					initialFiles={imageLink}
					identifier={'imageLink'}
					setValue={setValue}
					compression={IMAGE_TYPES['coupon'].compression}
					filesLimit={1}
				/>
			</FormContainer>
		</PageContainer>
	);
};

export default Coupon;
